<script setup lang="ts">
/**
 * The human-in-the-loop prompt, ported from the live viewer's InlineToolControls.
 *
 * One deliberate change from the original: this **emits** rather than calling
 * the API itself. The original posted straight to /v1/tool-approvals, which
 * makes a component impossible to drive from a fake transport and hard-wires it
 * to one backend. Routing decisions out through `useChat` to the transport is
 * what lets this render identically whether it is talking to A2A or to a script.
 */
import { ref } from 'vue';
import { Button } from '@asabdev/asab-ui';

import type { PendingApproval } from '../types/message';

defineProps<{
  approval: PendingApproval;
  busy?: boolean;
}>();

const emit = defineEmits<{
  approve: [];
  deny: [];
  'submit-result': [output: string, isError: boolean];
}>();

const outputText = ref('');
const isError = ref(false);

function submitResult() {
  emit('submit-result', outputText.value, isError.value);
  outputText.value = '';
  isError.value = false;
}
</script>

<template>
  <!-- A prompt appearing mid-stream must be reachable without stealing focus
       from the input someone may be typing in, so it announces politely rather
       than grabbing focus. -->
  <div
    data-testid="approval-prompt"
    role="group"
    aria-live="polite"
    :aria-label="approval.kind === 'tool_approval' ? 'Approval required' : 'Tool result required'"
    class="space-y-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-3 dark:border-amber-700 dark:bg-amber-900/10"
  >
    <div class="flex items-center justify-between">
      <p class="text-sm font-medium text-amber-800 dark:text-amber-300">
        {{ approval.kind === 'tool_approval' ? 'Approval required' : 'Tool result required' }}
      </p>
      <span class="font-mono text-xs text-amber-700 dark:text-amber-400">{{
        approval.toolName
      }}</span>
    </div>

    <pre
      v-if="approval.argumentsJson"
      data-testid="approval-args"
      class="max-h-32 overflow-y-auto whitespace-pre-wrap break-words rounded bg-white/60 p-2 text-xs text-gray-600 dark:bg-black/20 dark:text-gray-300"
      >{{ approval.argumentsJson }}</pre
    >

    <div v-if="approval.kind === 'tool_approval'" class="flex gap-2">
      <Button size="sm" data-testid="approval-approve" :disabled="busy" @click="emit('approve')">
        Approve
      </Button>
      <Button
        variant="danger"
        size="sm"
        data-testid="approval-deny"
        :disabled="busy"
        @click="emit('deny')"
      >
        Deny
      </Button>
    </div>

    <div v-else class="space-y-2">
      <textarea
        v-model="outputText"
        data-testid="approval-output"
        rows="3"
        placeholder="Tool output..."
        class="w-full rounded border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
      />
      <label class="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
        <input v-model="isError" type="checkbox" class="rounded" />
        Report as error
      </label>
      <Button
        size="sm"
        data-testid="approval-submit"
        :disabled="busy || !outputText.trim()"
        @click="submitResult"
      >
        Submit
      </Button>
    </div>
  </div>
</template>
